/**
 * Red exclusion rules for BUDOVA „Objekty k připojení“ / WF table check columns.
 * Shared by web UI tables and Tabulka_WF Excel (browser + Google Drive sync).
 *
 * Manual HP = `pripojovany_objekt_spec.hp_count_total` only (not acquisition import alone).
 * If manual HP is missing, HP-based red rules do not apply (cells stay empty).
 */

import type { BudovaBuildingType } from '../constants/object-workflow/budova-building-type.constants';
import { isBudovaBuildingType } from '../constants/object-workflow/budova-building-type.constants';
import { isBudovaNepripojenyPhase } from '../constants/object-workflow/budova-milestone-engine';
import { PHASE_CODE } from '../constants/object-workflow/phase-codes';
import { isSamePhase } from '../constants/object-workflow/workflow-model';
import { isVsoHp, VSO_HP_MIN } from './budova-hp-display.util';

/** Checkbox / WF marker columns that can be green (done), empty, or red (excluded). */
export const BUDOVA_PRIPOJENI_CHECK_COLUMNS = [
  'zavrtany',
  'vr',
  'digitalniCesta',
  'odboceno',
  'babovka',
] as const;

export type BudovaPripojeniCheckColumn = (typeof BUDOVA_PRIPOJENI_CHECK_COLUMNS)[number];

export type BudovaPripojeniExclusions = Record<BudovaPripojeniCheckColumn, boolean>;

/** Normalized WF template header → exclusion column (after `normalizeTemplateHeader`). */
export const WF_HEADER_TO_PRIPOJENI_CHECK_COLUMN: Record<string, BudovaPripojeniCheckColumn> = {
  babovka: 'babovka',
  zavrtano: 'zavrtany',
  sloupek: 'vr',
  'digitalni cesta': 'digitalniCesta',
  odbocka: 'odboceno',
};

/** Light red fill for excluded WF cells (ARGB). */
export const BUDOVA_PRIPOJENI_EXCLUSION_FILL_ARGB = 'FFFFCDD2';

export interface BudovaPripojeniExclusionInput {
  buildingType?: string | null;
  /**
   * Manual HP from building spec (`hpCountTotal`). Acquisition-only HP must not be passed —
   * HP-based red rules stay inactive until the user (or Specifikovat) has filled spec HP.
   */
  manualHpCount?: number | null;
  /** Object workflow status — Zrušený phase marks every check column red. */
  status?: string | null;
}

function emptyExclusions(value: boolean): BudovaPripojeniExclusions {
  return {
    babovka: value,
    zavrtany: value,
    vr: value,
    digitalniCesta: value,
    odboceno: value,
  };
}

/** True when status is the cancelled / Zrušený BUDOVA phase. */
export function isBudovaZrusenaPhase(status: string | null | undefined): boolean {
  return isSamePhase(status, PHASE_CODE.BUDOVA_ODMITNUTY);
}

/**
 * Resolve manual HP for exclusion rules: only a finite non-negative spec `hpCountTotal`.
 * Returns null when not yet entered (do not fall back to acquisition).
 */
export function resolveManualHpForPripojeniExclusion(
  specHpCountTotal: number | null | undefined,
): number | null {
  if (specHpCountTotal == null || !Number.isFinite(specHpCountTotal)) return null;
  const n = Math.trunc(Number(specHpCountTotal));
  if (n < 0) return null;
  return n;
}

/**
 * Which check columns are excluded (red) for the given building type / manual HP / phase.
 * Excluded columns must stay empty (unchecked) in UI and blank in WF export.
 */
export function resolveBudovaPripojeniExclusions(
  input: BudovaPripojeniExclusionInput,
): BudovaPripojeniExclusions {
  if (isBudovaZrusenaPhase(input.status)) {
    return emptyExclusions(true);
  }

  const rawType = (input.buildingType ?? '').trim();
  const buildingType: BudovaBuildingType | null = isBudovaBuildingType(rawType) ? rawType : null;

  if (buildingType === 'ZAMITNUTA') {
    // Typ Nerealizované (ZAMITNUTA) / Zrušená — všechna zaškrtávací pole červená.
    return emptyExclusions(true);
  }

  const out = emptyExclusions(false);
  if (!buildingType) return out;

  const statusKnown = (input.status ?? '').trim().length > 0;
  const pastNepripojeny = statusKnown && !isBudovaNepripojenyPhase(input.status);

  if (buildingType === 'BABOVKA') {
    out.zavrtany = true;
    out.vr = true;
    out.digitalniCesta = true;
  }

  if (buildingType === 'PRIPOJENA') {
    out.babovka = true;
    const hp = resolveManualHpForPripojeniExclusion(input.manualHpCount);
    if (hp != null) {
      // VSO (HP > 3 / ≥ 4): Digitální cesta excluded. MSO (HP < 4): VR excluded.
      if (isVsoHp(hp)) {
        out.digitalniCesta = true;
      } else if (hp < VSO_HP_MIN) {
        out.vr = true;
      }
    }
  }

  // VR / Závrt / Bábovka are only for Nepřipojený. Odbočeno stays visible after PHASE.1+
  // so the table matches the map stripe (`isOdbocenoComplete` / live branch status).
  if (pastNepripojeny) {
    out.zavrtany = true;
    out.vr = true;
    out.babovka = true;
  }

  return out;
}

export function isBudovaPripojeniColumnExcluded(
  exclusions: BudovaPripojeniExclusions,
  column: BudovaPripojeniCheckColumn,
): boolean {
  return exclusions[column] === true;
}

/** Map normalized WF header to exclusion column, if applicable. */
export function resolveWfHeaderExclusionColumn(
  headerNorm: string,
): BudovaPripojeniCheckColumn | null {
  return WF_HEADER_TO_PRIPOJENI_CHECK_COLUMN[headerNorm] ?? null;
}
